import React from "react";
import PreviewImage01 from "../../../../assets/images/home01.webp";
import PreviewImage02 from "../../../../assets/images/quiz.webp";
import PreviewImage03 from "../../../../assets/images/photos.webp";
import PreviewImage04 from "../../../../assets/images/lraderboard.webp";
import PreviewImage05 from "../../../../assets/images/videos.webp";
import Image from "../../../elements/Image";
import Title from "../../../elements/Title";
import { classNames } from "../../../../helpers/classNames";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { postFrontDetails } from "../../../../services/postService";
import { toast } from "react-toastify";
import { lngDetect } from "../../../common/lngDetect";

const getFloatAnimation = (index) => {
  const amplitudes = [10, 20, 30, 20, 10];
  const durations = [3.5, 4.6, 5.0, 4.6, 3.5];
  return {
    initial: { y: 40, opacity: 0 },
    animate: {
      y: [0, -amplitudes[index], 0, amplitudes[index], 0],
      opacity: 1,
      transition: {
        y: {
          duration: durations[index],
          repeat: Infinity,
          ease: "easeInOut",
        },
        opacity: { duration: 0.8 },
      },
    },
  };
};

const container = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.15, duration: 0.8, ease: "easeOut" },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const Preview = () => {
  const { data: previewData } = useQuery({
    queryKey: ["front-preview-section"],
    queryFn: async () => {
      const res = await postFrontDetails({
        slug: "preview-section",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const previewConfig = [
    {
      itemClasses: "z-[1] hover:z-[4] -mr-16",
      scale: "scale-75 origin-right group-hover:scale-90",
    },
    {
      itemClasses: "z-[2] hover:z-[4] -mr-8",
      scale: "scale-90 origin-right group-hover:scale-100",
    },
    {
      itemClasses: "z-[3] hover:z-[4]",
      scale: "scale-100 group-hover:scale-110",
    },
    {
      itemClasses: "z-[2] hover:z-[4] -ml-8",
      scale: "scale-90 origin-left group-hover:scale-100",
    },
    {
      itemClasses: "z-[1] hover:z-[4] -ml-16",
      scale: "scale-75 origin-left group-hover:scale-90",
    },
  ];

  const newPreviewData = {
    title: lngDetect(previewData?.content)?.title,
    description: lngDetect(previewData?.content)?.subTitle,
    previews: (previewData?.mediaGallery || [])
      .slice(0, 5)
      .map((item, index) => ({
        image: item.url,
        itemClasses: previewConfig[index]?.itemClasses || "",
        scale: previewConfig[index]?.scale || "",
      })),
  };

  return (
    <section className="relative py-10 lg:py-20 z-0" id="preview">
      {/* Background Glow */}
      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1], scale: [1, 1.2, 1] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="hidden lg:block absolute -top-[25vh] -left-[25vh] h-screen aspect-square bg-electric-violet/50 rounded-full blur-3xl z-[-1]"
      />

      <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-4">
        <motion.div
          className="relative"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="space-y-16  relative">
            {/* Subtitle */}
            <motion.div
              className="flex items-center justify-center"
              variants={item}
            >
              <Title title={newPreviewData.description} />
            </motion.div>

            {/* Title */}
            <motion.div
              className="mx-auto w-full max-w-2xl xl:max-w-3xl text-center"
              variants={item}
            >
              <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold leading-normal">
                {newPreviewData.title}
              </h2>
            </motion.div>

            {/* Previews */}
            <div className="flex items-center justify-center">
              {newPreviewData.previews.map((preview, index) => (
                <motion.div
                  key={index}
                  className={classNames(
                    "group relative w-80 flex",
                    preview.itemClasses
                  )}
                  variants={getFloatAnimation(index)}
                  initial="initial"
                  animate="animate"
                  transition={{ delay: index * 0.2 }}
                >
                  <div
                    className={classNames(
                      "relative flex w-full rounded-3xl sm:rounded-4xl xls:rounded-5xl overflow-hidden transition-all duration-300",
                      preview.imageClasses,
                      preview.scale,
                      preview.shadows
                    )}
                  >
                    <Image
                      preload
                      src={preview.image}
                      alt="App Preview"
                      effect="blur"
                      classes="object-contain"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Preview;
